import type {IDocumentData} from "@buildwithflux/models";
import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import {create} from "zustand";
import {devtools} from "zustand/middleware";

import {useFluxServices} from "injection/hooks";

import {buildDocumentStateUpdate, createStorePrimitives} from "./documentMutations";
import {type DocumentServices, type ActionEventBus} from "./serviceNotifier";
import {
    createClipboardSlice,
    createCommentSlice,
    createCompositeSlice,
    createDocumentDataSlice,
    createElementSlice,
    createMiscSlice,
    createPcbSlice,
    createPermissionSlice,
    createPropertySlice,
    createSelectionSlice,
    createSimulationSlice,
    createSubjectSlice,
    createUndoRedoSlice,
} from "./slices";
import type {SliceDependencies} from "./slices";
import {
    type CurrentUserProvider,
    type DocumentActionWithOptionalPayload,
    type DocumentStore,
    type DocumentStoreState,
    type IUndoRedoChange,
    type UseDocumentStore,
} from "./types";
import {createUndoRedoStore, type UndoRedoStore} from "./undoRedoStore";

// Re-export types for backward compatibility
export type {
    DocumentActionWithOptionalPayload,
    DocumentStore,
    DocumentStoreState,
    IUndoRedoChange,
    UseDocumentStore,
} from "./types";
export type {UndoRedoStore, UndoRedoStoreState} from "./undoRedoStore";
export {createUndoRedoStore} from "./undoRedoStore";

// Re-export selection helpers for backward compatibility
export {handleSelectObjectsNatively, isSelectObjectsAction} from "./selectionHelpers";

const documentInitialState: IDocumentData | null = null;

/**
 * Creates the vanilla non-bound-to-React document store.
 *
 * Action implementations are organized into domain-specific slice files under ./slices/.
 * This function wires them together into a single Zustand store instance.
 *
 * The undoRedoStore parameter is the separate vanilla store for undo/redo stacks,
 * which avoids triggering React re-renders on every document mutation.
 */
export function createDocumentStore(
    undoRedoStore: UndoRedoStore,
    currentUserService?: CurrentUserProvider,
    services: DocumentServices = {},
    actionEventBus?: ActionEventBus,
): UseDocumentStore {
    return create<DocumentStoreState>()(
        devtools(
            (set, get): DocumentStoreState => {
                const primitives = createStorePrimitives(
                    set,
                    get,
                    undoRedoStore,
                    currentUserService,
                    services,
                    actionEventBus,
                );

                const deps: SliceDependencies = {
                    primitives,
                    set,
                    get,
                    undoRedoStore,
                    currentUserService,
                    services,
                    actionEventBus,
                };

                return {
                    document: documentInitialState,

                    reset: () => {
                        set(buildDocumentStateUpdate(documentInitialState), false, "document/reset");
                        undoRedoStore.getState().resetUndoRedo();
                    },

                    ...createUndoRedoSlice(deps),
                    ...createSelectionSlice(deps),
                    ...createPropertySlice(deps),
                    ...createCommentSlice(deps),
                    ...createPermissionSlice(deps),
                    ...createSubjectSlice(deps),
                    ...createSimulationSlice(deps),
                    ...createElementSlice(deps),
                    ...createClipboardSlice(deps),
                    ...createDocumentDataSlice(deps),
                    ...createPcbSlice(deps),
                    ...createMiscSlice(deps),
                    ...createCompositeSlice(deps),
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "DocumentStore"},
        ),
    );
}

/**
 * Creates both the raw store and the bound hook from the same store instance,
 * along with the separate undo/redo vanilla store.
 */
export function createDocumentStoreAndHook(
    currentUserService?: CurrentUserProvider,
    services: DocumentServices = {},
    actionEventBus?: ActionEventBus,
): {store: DocumentStore; hook: UseDocumentStore; undoRedoStore: UndoRedoStore} {
    const undoRedoStore = createUndoRedoStore();
    const store = createDocumentStore(undoRedoStore, currentUserService, services, actionEventBus);
    return {store, hook: store, undoRedoStore};
}

// ============================================================================
// React hooks for accessing document state
// ============================================================================

export function useDocumentReset(): () => void {
    return useFluxServices().useDocumentStore((state) => state.reset);
}

// ============================================================================
// Re-exports from selectors for backward compatibility
//
// These pure functions were originally defined inline in this file.
// They have been moved to dedicated selector modules for better organization.
// The re-exports here preserve backward compatibility for existing consumers.
// ============================================================================

export {computeSelectionUidsState} from "./selectionState";
export type {ISelectionUidsStateFromStore, SelectionUidsState} from "./selectionState";
export {
    computeDocumentConfigSectionWithOwnerDefault,
    computeDocumentHasAdvancedPermissions,
} from "./selectors/document/derived";
export {
    computeElementPropertyValues,
    computeElementVisibilityToggleMap,
    computeMergedVisibilityToggleMap,
    computeSymbolDesignatorSuffixes,
} from "./selectors/elements/derived";
export {
    computeIsLockedByInheritance,
    computeTopLevelPcbFootprintNodes,
    computeTopLevelPcbLayoutNodes,
} from "./selectors/pcb/derived";
export {
    computeNonBranchPointElements,
    computeSelectedElements,
    computeSelectedLockedElementUids,
    computeSelectedPcbNodesSupportRotation,
    computeSingleSelectedPcbNodeUid,
} from "./selectors/selection/derived";
export {mergeSelectedSubjectProperties} from "./hooks/subjects";

// Re-export property types and functions from core for backward compatibility
export {getPropertyKey} from "@buildwithflux/core";
export type {MixedOrSingleProperty} from "@buildwithflux/core";

// Re-export PCB layout node utility functions
export {getLayerColor, getShapeFields, isThroughHole} from "./pcbLayoutNodeUtils";
